/**
 * KÖRVONAL — cart upsell ladder controller.
 *
 * Ours entirely. The thinking lives in sections/korvonal-cart-rungs.liquid,
 * which server-renders the panels and publishes the item count (bundles counted
 * as their three products) on data attributes. This file only:
 *
 *   1. re-fetches that section after every cart change,
 *   2. adds the gift when the count reaches 4 and takes it back out below 4
 *      (the tote by default; with the chooser live, whatever `_gift_choice`
 *      names — the section resolves the choice, this file adds blindly),
 *   3. fills the "one more product" panel with a recommendation,
 *   4. performs the two-games-for-a-bundle swap.
 *
 * WHY THE TOTE HAS TO BE ADDED BY US
 *   A Shopify BXGY never auto-adds the Y item — the customer has to find it and
 *   add it themselves. The 2026-03 → 2026-08 run of exactly this gift proves the
 *   cost: 18 totes delivered across 247 qualifying orders, 7%. The discount
 *   underneath (20.000 Ft floor, `körvonal ajándék`) only zeroes the
 *   price; qualification and the add are ours.
 *
 * THE DECLINE GUARD — cart attribute `_gift_tote`
 *   Auto-adding on every cart change would fight a customer who deleted the
 *   tote: they remove it, the next cart event puts it back, forever. So we
 *   record what we did.
 *
 *     ''          nothing added yet
 *     'auto'      we put it in
 *     'declined'  it was 'auto', then vanished while the cart still qualified,
 *                 i.e. they took it out on purpose — never re-add
 *
 *   'declined' is cleared when the cart empties, so a later, unrelated cart
 *   gets offered the gift again.
 *
 * THE FLAG MUST NEVER OUTLIVE THE TOTE
 *   `_gift_tote` rides to the order, so a flag that disagrees with the line
 *   items is a lie in the permanent record — and worse, `auto` is what stops us
 *   re-offering, so a stuck `auto` silently cancels the gift for the rest of
 *   the session. Every decision about whether the tote is in the cart, and
 *   under which line key, is therefore read from /cart.js. The panel's own
 *   count is only trusted once its `data-cart-total` agrees with that cart;
 *   see agreeingPanel().
 */
(function () {
  'use strict';

  var SLOT = '#kv-rungs-slot';
  var SECTION = 'korvonal-cart-rungs';
  var ATTR = '_gift_tote';
  var EXCLUDE_SKUS = ['KVV01']; // the COD fee is a line item, not a product
  var busy = false;
  var passes = 0; // a cart mutation triggers another refresh; bound the chain
  var docBound = false; // the document-level cart listeners are bound once

  function slot() {
    return document.querySelector(SLOT);
  }

  function panel() {
    return document.querySelector('[data-kv-rungs]');
  }

  /**
   * The store writes 7.900 Ft. Intl with hu-HU writes 7900 — Hungarian does not
   * group four-digit numbers — so the one price rendered by JS came out looking
   * foreign next to every price rendered by Liquid. Group explicitly.
   */
  function money(cents) {
    var n = Math.round(cents / 100);
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ' Ft';
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  function setAttributes(attrs) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    });
  }

  function setAttribute(value) {
    var attrs = {};
    attrs[ATTR] = value;
    return setAttributes(attrs);
  }

  /** Pull a fresh render of the section and swap it in. */
  function render() {
    var host = slot();
    if (!host) return Promise.resolve();
    // The cache-buster is not optional. Shopify will happily serve a cached
    // render of this section, and since the panel publishes the cart's own
    // counts, a stale one means the drawer reasons about a cart that no longer
    // exists — the symptom was data-owned arriving empty from a render made
    // before that attribute existed.
    return fetch('?section_id=' + SECTION + '&_=' + Date.now(), {
      headers: { Accept: 'text/html', 'Cache-Control': 'no-cache' }
    })
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-kv-rungs]');

        // Replace the PANEL, never the slot's contents.
        //
        // The section ships its own <style> as a sibling of the panel, so
        // `host.innerHTML = panel` threw the stylesheet away on the first cart
        // change and every rung after that rendered raw — image at full size,
        // no card, no button. It looked like the drawer had fallen apart, and
        // it did, one refresh in.
        var existing = host.querySelector('[data-kv-rungs]');
        if (fresh) {
          if (existing) existing.outerHTML = fresh.outerHTML;
          else host.appendChild(document.importNode(fresh, true));
        } else if (existing) {
          existing.innerHTML = '';
        }

        // A slot the server did not pre-render — the cart page — has no styles
        // of its own, and the panel's CSS ships inside the section. Carry it
        // across once, or the rungs land there raw.
        if (fresh && !host.querySelector('style')) {
          var css = doc.querySelector('#shopify-section-' + SECTION + ' style');
          if (css) host.appendChild(document.importNode(css, true));
        }
        applyPreview();
        placeWideRung();
        bindPanel();
        hideLegacySlot();
        applyExperiment();
        trackGiftRung();
        trackMultibuyRung();
        trackBundleRung();
        // Every panel replacement wipes whatever the suggestion filled in, so
        // the fill belongs to the render rather than to the end of one refresh
        // cycle: two overlapping refreshes used to leave the row — and the
        // fallback link — blanked by the later render.
        fillRecommendation();
      })
      .catch(function () {
        /* a failed refresh leaves the previous panel standing — never blocks the cart */
      });
  }

  /** In `preview` mode the panel renders hidden; ?ajandek=1 opts in. */
  function applyPreview() {
    var el = panel();
    if (!el) return;
    if (el.getAttribute('data-mode') === 'preview' && optedIn()) {
      el.removeAttribute('hidden');
    }
    // The gift chooser has its own preview, independent of the rung's: the
    // rung can be live (`on`) while the chooser is still `preview`. The server
    // renders both copy variants then — the signed-off tote copy visible, the
    // chooser hidden — and only an opted-in visitor gets them swapped.
    if (el.getAttribute('data-chooser') === 'preview' && optedIn()) {
      el.querySelectorAll('[data-kv-choose]').forEach(function (n) {
        n.removeAttribute('hidden');
      });
      el.querySelectorAll('[data-kv-solo]').forEach(function (n) {
        n.setAttribute('hidden', '');
      });
    }
    // The bundle cart's game row has its own switch and opt-in
    // (`custom.bundle_rung_mode`, ?csomagplusz=1). Opted in, the Shopify-filled
    // row is removed rather than hidden: fillRecommendation() un-hides it.
    if (el.getAttribute('data-br-mode') === 'preview' && brOptedIn()) {
      el.querySelectorAll('[data-kv-brung]').forEach(function (n) {
        n.removeAttribute('hidden');
      });
      el.querySelectorAll('[data-kv-brung-off]').forEach(function (n) {
        n.remove();
      });
    }
    // The 2+1 rung previews on the same opt-in; it is static (no cart
    // mutation), so revealing it is the whole of its preview.
    if (el.getAttribute('data-multibuy') === 'preview' && optedIn()) {
      el.querySelectorAll('[data-kv-multibuy]').forEach(function (n) {
        n.removeAttribute('hidden');
      });
    }
    // `on` is only on inside the discount's window (data-mb-start/end, unix
    // seconds, baked by the section from the same dates as the BXGY). The
    // section already degrades `on` to `preview` server-side; this repeats
    // the check on the client so a render cached across midnight — the
    // last evening's, or the first morning's — shows the right thing.
    if (el.getAttribute('data-multibuy') === 'on' && !optedIn()) {
      var s = Number(el.getAttribute('data-mb-start')), e = Number(el.getAttribute('data-mb-end'));
      var now = Date.now() / 1000;
      if (s && e && (now < s || now > e)) {
        el.querySelectorAll('[data-kv-multibuy]').forEach(function (n) {
          n.setAttribute('hidden', '');
        });
      }
    }
  }

  /**
   * Whether the CHOOSER (not the rung) is live for this visitor. Gates the
   * select handler the same way active() gates the cart mutations: in chooser
   * preview a non-opted visitor never sees the select, but a cached render
   * must not let a stray change event write `_gift_choice` either.
   */
  function chooserActive() {
    var el = panel();
    if (!el) return false;
    var m = el.getAttribute('data-chooser');
    if (m === 'on') return true;
    return m === 'preview' && optedIn();
  }

  function optedIn() {
    return /[?&]ajandek=1/.test(window.location.search);
  }

  /**
   * The bundle-cart picks' preview opt-in. Sticky for the tab, unlike
   * ?ajandek=1: the bundle is added on a PDP or a collection, and the param
   * would not survive the click from one to the other.
   */
  function brOptedIn() {
    try {
      if (/[?&]csomagplusz=1/.test(window.location.search)) sessionStorage.setItem('kv_csomagplusz', '1');
      return sessionStorage.getItem('kv_csomagplusz') === '1';
    } catch (e) {
      return /[?&]csomagplusz=1/.test(window.location.search);
    }
  }

  function brActive() {
    var el = panel();
    if (!el || !active()) return false;
    var m = el.getAttribute('data-br-mode');
    if (m === 'on') return true;
    return m === 'preview' && brOptedIn();
  }

  /**
   * Whether we may touch the cart at all.
   *
   * Hiding the panel is NOT enough on its own: in `preview` the section still
   * renders (hidden) and this file still loads, so without this gate every HU
   * visitor who reached 4 products would have a tote quietly added to their
   * cart — at full price, since the discount is armed separately. Preview has
   * to mean "nothing happens", not "nothing is visible".
   */
  function active() {
    var el = panel();
    if (!el) return false;
    var mode = el.getAttribute('data-mode');
    if (mode === 'on') return true;
    return mode === 'preview' && optedIn();
  }

  /**
   * Find the gift in the cart. The gift line carries the `_gift` line-item
   * property since the chooser shipped — the property is what separates a
   * gifted book from the same book bought as a product (an add without it
   * merges into the paid line, and then no rule can tell them apart).
   *
   * The bare variant match is the pre-chooser tote, still present in carts
   * opened before this deployed. It matches ONLY the legacy (tote) variant:
   * widened to the chosen variant it would claim a paid book as the gift and
   * silently cancel the offer. Returns the line, or null.
   */
  function giftLine(cart, variantId, legacyId) {
    var items = cart.items || [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].properties && items[i].properties._gift) return items[i];
    }
    var legacy = Number(legacyId || variantId);
    if (!legacy) return null;
    for (var j = 0; j < items.length; j++) {
      if (Number(items[j].variant_id) === legacy) return items[j];
    }
    return null;
  }

  /**
   * The panel that agrees with THIS cart, or null.
   *
   * Everything the panel publishes about the cart — the count, whether the tote
   * is in it, that tote's line key — describes the cart as it was when Shopify
   * rendered it. A cached or superseded render describes a cart that no longer
   * exists, and reasoning across the two is what put `_gift_tote=auto` on an
   * order that shipped without a tote (#KV56320, 2026-08-20): the panel still
   * said the tote was in the cart, so `!inCart` was false, the "they took it
   * out" branch never ran, and the flag was never corrected.
   *
   * `cart.total_price` is the one number both sides publish, so it is the
   * cheapest possible agreement check. One re-render, then give up on the pass.
   * A skipped pass decides the gift one cart event later, which the next event
   * fixes; a pass run against a stale panel writes a lie into the order.
   */
  function agreeingPanel(cart, retried) {
    var el = panel();
    if (!el) return Promise.resolve(null);
    if (String(el.getAttribute('data-cart-total')) === String(cart.total_price)) {
      return Promise.resolve(el);
    }
    if (retried) return Promise.resolve(null);
    return render().then(function () {
      return agreeingPanel(cart, true);
    });
  }

  /**
   * The flash hour's state as the CLOCK says it should be now, or null when no
   * flash is configured (`custom.gift_flash`, see the section). The section
   * decides on/grace/off at render time, so a drawer rendered at 19:59 still
   * says `on` at 00:40 if the cart has not changed — and the discount that
   * zeroes the book ended at 00:30.
   */
  function flashExpected(el) {
    var from = Number(el.getAttribute('data-flash-from')) || 0;
    if (!from) return null;
    var to = Number(el.getAttribute('data-flash-to')) || 0;
    var until = Number(el.getAttribute('data-flash-until')) || 0;
    var now = Date.now() / 1000;
    if (now >= from && now < to) return 'on';
    if (now >= to && now < until) return 'grace';
    return 'off';
  }

  /**
   * Reconcile the cart against the threshold. At most one mutation per pass,
   * and every mutation re-renders, so the next pass sees the settled cart.
   */
  function reconcile(flashRetried) {
    if (!panel() || !active()) return Promise.resolve();

    return getCart().then(function (cart) {
      return agreeingPanel(cart).then(function (el) {
        if (!el) return null;

        // A render from the other side of a flash boundary: re-render once.
        // If the answer is still stale, fail toward not giving: past the
        // window a gift we added comes back out (the discount no longer pays
        // for it); short of it, nothing is added.
        var expected = flashExpected(el);
        var rendered = el.getAttribute('data-flash') || 'off';
        var flashStandDown = false;
        var flashNoAdd = rendered === 'grace';
        if (expected && expected !== rendered) {
          if (flashRetried !== true) {
            return render().then(function () {
              return reconcile(true);
            });
          }
          if (expected === 'off') flashStandDown = true;
          else flashNoAdd = true;
        }

        var count = parseInt(el.getAttribute('data-count'), 10) || 0;
        var threshold = parseInt(el.getAttribute('data-threshold'), 10) || 4;
        var variantId = el.getAttribute('data-gift-variant');
        var legacyId = el.getAttribute('data-gift-legacy');
        var floor = parseInt(el.getAttribute('data-floor'), 10) || 0;
        if (flashStandDown) floor = Infinity;
        var state = (cart.attributes && cart.attributes[ATTR]) || '';

        // Read from the cart, never from data-gift-in-cart / data-gift-key.
        // Those are the first thing to go stale, and they are exactly the two
        // inputs that decide whether we add, remove, or stand down.
        var line = giftLine(cart, variantId, legacyId);
        var inCart = !!line;
        var giftKey = line ? line.key : '';

        if (!cart.items.length) {
          return state ? setAttribute('') : null;
        }

        // Qualifies, gift missing. BOTH gates: the count is ours, the floor is
        // the discount's. Clear one without the other and the "gift" arrives with
        // a price on it.
        if (count >= threshold && cart.total_price >= floor && !inCart) {
          if (flashNoAdd) return null;
          if (state === 'declined') return null;
          if (state === 'auto') {
            // They took our gift back out. This is the only unambiguous decline
            // the ladder can observe, and it is what separates "the offer does
            // not work" from "nobody was ever shown it".
            //
            // It is also only trustworthy now that `inCart` comes from the cart
            // rather than the panel: a stale panel used to skip this branch
            // entirely, so a removed tote logged nothing and left the flag on
            // `auto`. The event and the flag were wrong together.
            track('gift_tote_removed', 'tote-out-' + dayKey(), String(variantId));
            return setAttribute('declined');
          }
          // The `_gift` property keeps this line separate from the same
          // variant bought as a product — see giftLine. Underscore-prefixed,
          // so checkout never shows it.
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: [{ id: Number(variantId), quantity: 1, properties: { _gift: '1' } }]
            })
          })
            .then(function (r) {
              if (!r.ok) throw new Error('gift add failed');
              track('gift_tote_added', 'tote-' + dayKey(), String(variantId));
              return setAttribute('auto');
            })
            .catch(function () {
              /* out of stock or a race — leave the cart exactly as the customer left it */
            });
        }

        // Below the threshold the whole thing resets.
        //
        // Taking our own gift back out is the obvious half. The other half is
        // clearing the flag, and it has to happen HERE rather than only on an
        // empty cart: /cart/clear.js empties the line items but leaves the
        // attributes untouched, so a `declined` from an earlier cart would ride
        // into a brand-new one and suppress the offer for the rest of the
        // session. Falling below 4 ends the offer that was declined, so the next
        // climb back up is a fresh one.
        if (count < threshold || cart.total_price < floor) {
          if (inCart && state === 'auto' && giftKey) {
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: giftKey, quantity: 0 })
            }).then(function () {
              return setAttribute('');
            });
          }
          // A tote the customer added themselves stays; only the flag is reset.
          return state ? setAttribute('') : null;
        }

        return null;
      });
    });
  }

  /**
   * The "one more product" panel gets a real suggestion from Shopify's own
   * recommendations, filtered to things the cart can actually use.
   */
  function fillRecommendation() {
    var box = document.querySelector('[data-kv-rec]');
    if (!box || !active()) return Promise.resolve();

    return getCart().then(function (cart) {
      if (!cart.items.length) return;
      var anchor = cart.items.slice().sort(function (a, b) {
        return b.line_price - a.line_price;
      })[0];
      var inCart = cart.items.map(function (i) {
        return i.product_id;
      });
      var giftVariant = panel() && panel().getAttribute('data-gift-variant');
      // Never suggest another csomag. The rung asks for ONE more product to
      // finish the gift; answering that with an 18.960 Ft bundle is a bigger
      // ask than the reward, and it reads as not listening.
      var bundleIds = (panel().getAttribute('data-bundle-ids') || '')
        .split(',')
        .filter(Boolean)
        .map(Number);
      // Not just what is in the cart — what the cart already CONTAINS. A bundle
      // brings three games with it, and suggesting one of them is asking the
      // customer to buy the same box twice.
      var owned = (panel().getAttribute('data-owned') || '')
        .split(',')
        .filter(Boolean)
        .map(Number);

      // `complementary` is the hand-curated list in Shopify admin, and it is
      // empty for every bundle today — which is how this panel first shipped
      // with no suggestion at all. Fall back to `related` so the slot is only
      // empty when Shopify genuinely has nothing to say.
      function ask(intent) {
        return fetch(
          '/recommendations/products.json?limit=8&intent=' + intent + '&product_id=' + anchor.product_id,
          { headers: { Accept: 'application/json' } }
        ).then(function (r) {
          return r.json();
        });
      }

      return ask('complementary')
        .then(function (data) {
          var got = (data && data.products) || [];
          return got.length ? data : ask('related');
        })
        .then(function (data) {
          var list = (data && data.products) || [];
          var pick = list.filter(function (p) {
            if (inCart.indexOf(p.id) !== -1) return false;
            if (owned.indexOf(p.id) !== -1) return false;
            if (bundleIds.indexOf(p.id) !== -1) return false;
            if (!p.available) return false;
            var v = p.variants && p.variants[0];
            if (v && String(v.id) === String(giftVariant)) return false;
            var sku = ((v && v.sku) || '').toUpperCase();
            if (EXCLUDE_SKUS.indexOf(sku) !== -1) return false;
            return true;
          })
            // Cheapest first: the copy asks for one more product, so the
            // suggestion should be the shortest path to it, not the priciest.
            .sort(function (a, b) {
              return (a.price || 0) - (b.price || 0);
            })[0];
          if (!pick) {
            var fb = document.querySelector('[data-kv-rec-fallback]');
            if (fb) fb.removeAttribute('hidden');
            return;
          }

          var variant = pick.variants[0];
          box.querySelector('[data-kv-rec-img]').src = pick.featured_image
            ? pick.featured_image + '&width=120'
            : '';
          box.querySelector('[data-kv-rec-img]').alt = pick.title;
          box.querySelector('[data-kv-rec-title]').textContent = pick.title;
          box.querySelector('[data-kv-rec-price]').textContent = money(variant.price);
          box.querySelector('[data-kv-rec-add]').setAttribute('data-variant', variant.id);
          box.removeAttribute('hidden');
        })
        .catch(function () {
          /* no recommendation is fine — the panel still says what the offer is */
        });
    });
  }

  function addVariant(id) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
    });
  }

  /** Swap two loose games for the bundle that contains them. */
  function doSwap(button) {
    var variant = button.getAttribute('data-variant');
    var keys = (button.getAttribute('data-remove') || '').split(',').filter(Boolean);
    button.setAttribute('disabled', 'disabled');
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    track('bundle_card_accepted', 'accepted', null);
    return addVariant(variant)
      .then(function (r) {
        if (!r.ok) throw new Error('bundle add failed');
        return keys.reduce(function (chain, key) {
          return chain.then(function () {
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: key, quantity: 0 })
            });
          });
        }, Promise.resolve());
      })
      .then(function () {
        refresh();
        reloadCartSection();
      })
      .catch(function () {
        button.removeAttribute('disabled');
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
      });
  }

  /**
   * Ask the vendor cart to redraw itself. component-cart.js owns the markup
   * inside #AjaxCartForm, so we re-fetch its section rather than touching it.
   */
  function reloadCartSection() {
    var host = document.getElementById('AjaxCartForm');
    if (!host) {
      window.location.reload();
      return;
    }
    fetch('?section_id=helper-cart', { headers: { Accept: 'text/html' } })
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var form = doc.getElementById('AjaxCartForm');
        var subtotal = doc.getElementById('AjaxCartSubtotal');
        if (form) host.innerHTML = form.innerHTML;
        if (subtotal && document.getElementById('AjaxCartSubtotal')) {
          document.getElementById('AjaxCartSubtotal').innerHTML = subtotal.innerHTML;
        }
        if (typeof host.ajaxifyCartItems === 'function') host.ajaxifyCartItems();
        bindCartEvents();
      })
      .catch(function () {
        /* leave the drawer as-is; the next cart event will redraw it */
      });
  }

  /**
   * Stand the vendor's fixed cross-sell slot down while our own dynamic bundle
   * card is live, or the drawer carries two cross-sells at once — and when the
   * fixed one is still pointed at the tote, they contradict each other outright.
   * The Liquid guard in korvonal-cart-upsell covers mode `on`; this covers the
   * operator's opted-in preview, so what preview shows IS the final drawer.
   */
  function hideLegacySlot() {
    if (!active()) return;
    document.querySelectorAll('.cart-individual-product-recommendation').forEach(function (el) {
      el.style.display = 'none';
    });
  }

  /* ---------------------------------------------------------------------
   * cart-bundle-card-v1 — is the dynamic bundle card worth the slot?
   *
   * Control sees the drawer with no cross-sell card at all. That is not quite
   * the historical status quo (a fixed tote card sat there), but that card
   * earned 2 adds in five months, so "nothing" is the honest counterfactual
   * and it isolates what this card does rather than comparing two cards.
   *
   * Speaks the harness protocol in lib/experiments/*, like korvonal-oto:
   *   numerator    `_experiment_cart` cart attribute → rides to the order
   *   denominator  one `experiment_exposure` per visitor, fired when the card
   *                is ELIGIBLE — the cart holds a game whose bundle is not in
   *                it yet — so the denominator is "could have seen it", equal
   *                in both arms. Firing on page view instead would drown the
   *                effect in visitors the card could never have reached.
   *
   * Its own attribute, not `_experiment`: that one belongs to oto-bundle-v1
   * and a second writer would clobber it, which is why the advisor test has
   * `_experiment_advisor`.
   * ------------------------------------------------------------------- */
  var EXPERIMENT = 'cart-bundle-card-v1';
  var TOKEN = 'cmpismusd0000b965jxyb4r46';
  var EVENTS_ENDPOINT = 'https://api.korvonal.com/api/storefront-events/' + TOKEN;
  var ARM_KEY = 'otto_exp_' + EXPERIMENT;
  var VISITOR_KEY = 'otto_visitor_id';
  var FIRED_KEY = 'otto_exp_fired_' + EXPERIMENT;
  var stamped = false;

  function cardMode() {
    var el = panel();
    return el ? el.getAttribute('data-card-mode') || 'off' : 'off';
  }

  /** Sticky per browser, so a returning visitor never flips arm mid-test. */
  function arm() {
    if (cardMode() !== 'ab') return 'treatment';
    try {
      var stored = localStorage.getItem(ARM_KEY);
      if (stored === 'control' || stored === 'treatment') return stored;
      var picked = Math.random() < 0.5 ? 'control' : 'treatment';
      localStorage.setItem(ARM_KEY, picked);
      return picked;
    } catch (e) {
      // Storage blocked (private mode): no sticky arm, so keep them in control
      // rather than showing a card we could not attribute.
      return 'control';
    }
  }

  function visitorId() {
    try {
      var v = localStorage.getItem(VISITOR_KEY);
      if (!v) {
        v =
          window.crypto && crypto.randomUUID
            ? crypto.randomUUID()
            : 'v-' + Date.now() + '-' + Math.random().toString(16).slice(2);
        localStorage.setItem(VISITOR_KEY, v);
      }
      return v;
    } catch (e) {
      return null;
    }
  }

  /**
   * Event ids upsert, so a day-scoped suffix collapses repeats into one row per
   * visitor per day. That makes `gift_rung_shown` a denominator the tote events
   * can be read against, instead of a count of how often the drawer re-rendered.
   */
  function dayKey() {
    return new Date().toISOString().slice(0, 10);
  }

  /**
   * The gift rung is rendered by Liquid, so "was it ever on screen" has no
   * natural event. Without this, zero totes is unreadable: it looks identical
   * whether nobody qualified or everyone declined.
   */
  var giftRungTracked = false;
  function trackGiftRung() {
    if (giftRungTracked || !active()) return;
    if (!document.querySelector('[data-kv-gift]')) return;
    giftRungTracked = true;
    track('gift_rung_shown', 'rung-' + dayKey(), null);
  }

  /**
   * The 2+1 rung is Liquid too, so its denominator needs the same treatment
   * as the gift rung's: one `multibuy_rung_shown` per visitor per day, and a
   * `multibuy_rung_click` when the link to the lander is taken. Read against
   * the lander's own offer_* funnel.
   */
  var mbTracked = false;
  function trackMultibuyRung() {
    if (mbTracked) return;
    var rung = document.querySelector('[data-kv-multibuy]');
    if (!rung || rung.hasAttribute('hidden')) return;
    mbTracked = true;
    var el = panel();
    track('multibuy_rung_shown', 'mb-' + dayKey(), el ? 'count-' + el.getAttribute('data-mb-count') : null);
  }
  /**
   * The bundle cart's picks: `bundle_rung_shown` once per visitor per day when
   * the rows are on screen, `bundle_rung_add` per kind (game). Its own
   * key, so they never read as cart-bundle-card-v1 traffic.
   */
  var brTracked = false;
  function trackBundleRung() {
    if (brTracked || !brActive()) return;
    var rows = document.querySelector('[data-kv-brung]');
    if (!rows || rows.hasAttribute('hidden')) return;
    brTracked = true;
    var kinds = [];
    rows.querySelectorAll('[data-kv-brung-add]').forEach(function (b) {
      kinds.push(b.getAttribute('data-kind'));
    });
    trackBr('bundle_rung_shown', 'br-' + dayKey(), kinds.join('+'));
  }
  function trackBr(name, idSuffix, reason) {
    var vid = visitorId();
    if (!vid) return;
    var el = panel();
    fetch(EVENTS_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        events: [
          {
            id: 'bundle-rung:' + vid + ':' + idSuffix,
            eventName: name,
            clientId: vid,
            occurredAt: new Date().toISOString(),
            data: { experimentKey: 'bundle-rung', variant: el ? el.getAttribute('data-br-mode') : null, reason: reason }
          }
        ]
      })
    }).catch(function () {
      /* never block the customer on bookkeeping */
    });
  }
  function track(name, idSuffix, extra) {
    var vid = visitorId();
    if (!vid) return;
    var data = { experimentKey: EXPERIMENT, variant: arm() };
    if (extra) data.reason = extra;
    fetch(EVENTS_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        events: [
          {
            id: EXPERIMENT + ':' + vid + (idSuffix ? ':' + idSuffix : ''),
            eventName: name,
            clientId: vid,
            occurredAt: new Date().toISOString(),
            data: data
          }
        ]
      })
    }).catch(function () {
      /* never block the customer on bookkeeping */
    });
  }

  /**
   * Apply the arm to the card, and record the visitor — both arms, always. An
   * experiment that only marks the treated arm cannot be read.
   */
  function applyExperiment() {
    var card = document.querySelector('[data-kv-card]');
    if (!card) return; // not eligible: no bundle to offer, so no exposure
    // Abroad the card is not part of cart-bundle-card-v1 (a HU test): no arm
    // stamp on the cart, no exposure, or foreign orders read as HU treatment.
    var host = panel();
    if (host && host.getAttribute('data-intl') === 'true') return;
    var a = arm();

    if (cardMode() === 'ab' && a === 'treatment') card.removeAttribute('hidden');

    if (!active()) return;

    if (!stamped) {
      stamped = true;
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { _experiment_cart: EXPERIMENT + ':' + a } })
      }).catch(function () {});
    }

    try {
      if (localStorage.getItem(FIRED_KEY)) return;
      localStorage.setItem(FIRED_KEY, '1');
    } catch (e) {}
    track('experiment_exposure', '', null);
  }

  /**
   * The cart page splits the ladder across its two columns: the bundle card
   * goes under the product list where it has room for the component images,
   * the gift rung stays beside the total. One fetch still owns both — the card
   * is moved rather than rendered twice, so there is no second source of truth.
   */
  function placeWideRung() {
    var wide = document.getElementById('kv-rungs-slot-wide');
    if (!wide) return;
    var card = document.querySelector('#kv-rungs-slot .kv-rung--swap');
    if (card) {
      wide.innerHTML = '';
      wide.appendChild(card);
    } else {
      wide.innerHTML = '';
    }
  }

  /**
   * The visitor picked a different gift. `_gift_choice` is the server-side
   * truth the next render resolves against; everything else follows from the
   * normal reconcile pass. If our gift is already in the cart, take it out and
   * clear the flag in the same update, so reconcile re-adds the new choice —
   * without the clear, `auto` would read as "already handled" and the swap
   * would end with no gift at all.
   */
  function onGiftChange(sel) {
    if (!chooserActive() || !active()) return;
    var chosen = sel.value;
    sel.setAttribute('disabled', 'disabled');
    return getCart()
      .then(function (cart) {
        var el = panel();
        var line = giftLine(
          cart,
          el && el.getAttribute('data-gift-variant'),
          el && el.getAttribute('data-gift-legacy')
        );
        var state = (cart.attributes && cart.attributes[ATTR]) || '';
        var attrs = { _gift_choice: chosen };
        var step = Promise.resolve();
        if (line && state === 'auto' && String(line.variant_id) !== String(chosen)) {
          attrs[ATTR] = '';
          step = fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: 0 })
          });
        }
        return step.then(function () {
          return setAttributes(attrs);
        });
      })
      .then(function () {
        refresh();
        reloadCartSection();
      })
      .catch(function () {
        sel.removeAttribute('disabled');
      });
  }

  function bindPanel() {
    var swap = document.querySelector('[data-kv-swap]');
    if (swap && !swap.dataset.bound) {
      swap.dataset.bound = '1';
      swap.addEventListener('click', function () {
        doSwap(swap);
      });
    }

    document.querySelectorAll('[data-kv-gift-select]').forEach(function (sel) {
      if (sel.dataset.bound) return;
      sel.dataset.bound = '1';
      sel.addEventListener('change', function () {
        onGiftChange(sel);
      });
    });

    var link = document.querySelector('[data-kv-mb-link]');
    if (link && !link.dataset.bound) {
      link.dataset.bound = '1';
      link.addEventListener('click', function () {
        track('multibuy_rung_click', 'mb-click-' + dayKey(), null);
      });
    }
    // The bundle cart's game: one add, then the usual gift reconcile.
    document.querySelectorAll('[data-kv-brung-add]').forEach(function (btn) {
      if (btn.dataset.bound) return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', function () {
        if (!brActive()) return;
        btn.setAttribute('disabled', 'disabled');
        btn.classList.add('is-loading');
        btn.setAttribute('aria-busy', 'true');
        var kind = btn.getAttribute('data-kind') || '';
        trackBr('bundle_rung_add', 'br-add-' + kind + '-' + dayKey(), kind);
        addVariant(btn.getAttribute('data-variant'))
          .then(function () {
            refresh();
            reloadCartSection();
          });
      });
    });

    var add = document.querySelector('[data-kv-rec-add]');
    if (add && !add.dataset.bound) {
      add.dataset.bound = '1';
      add.addEventListener('click', function () {
        add.setAttribute('disabled', 'disabled');
        add.classList.add('is-loading');
        add.setAttribute('aria-busy', 'true');
        addVariant(add.getAttribute('data-variant')).then(function () {
          refresh();
          reloadCartSection();
        });
      });
    }
  }

  /**
   * The vendor dispatches `cart-updated` on <cart-form> after a quantity change
   * and a non-bubbling `add-to-cart` on each product form (see component-cart.js
   * and korvonal-oto.liquid, which listens to the same event). Both elements are
   * replaced on re-render, so binding is re-run rather than done once.
   */
  function bindCartEvents() {
    document.querySelectorAll('cart-form').forEach(function (el) {
      if (el.dataset.kvBound) return;
      el.dataset.kvBound = '1';
      el.addEventListener('cart-updated', function () {
        refresh();
      });
    });
    document.querySelectorAll('[data-type="add-to-cart-form"]').forEach(function (form) {
      if (form.dataset.kvBound) return;
      form.dataset.kvBound = '1';
      form.addEventListener('add-to-cart', function () {
        refresh();
      });
    });

    // AND ON THE DOCUMENT (2026-09-09). The theme dispatches `add-to-cart` on
    // the <product-form> CUSTOM ELEMENT, not on the [data-type=…] <form> inside
    // it, so the per-form listener above never heard a PDP add — events do not
    // travel downward. The panel kept the count the page was rendered with,
    // zero, and the 2+1 rung was simply absent for the first products a
    // customer added: reported as "1 in the cart, nothing offered me the 2+1;
    // 2 in the cart, nothing said the third was free", and reproduced with the
    // server returning the right panel for the same cart the whole time.
    // Listening on the document catches the event wherever it is dispatched
    // from; the per-element binds stay because they are what fires first when
    // the drawer's own cart-form updates a line.
    if (!docBound) {
      docBound = true;
      // CAPTURE, not bubble. The theme's `add-to-cart` is dispatched with
      // bubbles:false, so a listener on the document never sees it on the way
      // up — only on the way down. This one character is the difference
      // between the rung appearing on the first product and not at all.
      document.addEventListener('add-to-cart', function () {
        refresh();
      }, true);
      document.addEventListener('cart-updated', function () {
        refresh();
      }, true);
    }
  }

  function refresh() {
    if (busy) return;
    if (passes > 3) return; // paranoia: never loop on a cart that will not settle
    busy = true;
    passes++;
    render()
      .then(function () {
        return reconcile(true); // freshly rendered: no second flash re-render
      })
      .then(function (mutated) {
        if (mutated) {
          busy = false;
          // Our own add/remove changed the lines: redraw the vendor list too, as
          // the chooser and the recommendation add do. Without it the drawer
          // showed the old lines and total until the next cart event.
          reloadCartSection();
          return render().then(fillRecommendation);
        }
        return fillRecommendation();
      })
      .catch(function () {})
      .then(function () {
        busy = false;
        passes = 0;
        bindCartEvents();
        hideLegacySlot();
        applyExperiment();
      });
  }

  /**
   * THE TWO MARKS OUTSIDE THE DRAWER — mocks 3/5 and 5/5, approved 2026-08-27.
   *
   * A pill above the add-to-cart button on an eligible product page, and a
   * "2+1" badge on every eligible product card. Both read the same three gates
   * the panel already applies (HU market, the ladder switch, the discount
   * window) plus the one baked handle list, published as data-mb-handles — so
   * a product the discount does not honour can never be marked, and on Sep 21
   * the marks leave with the rung. No second list to drift.
   *
   * Drawn client-side rather than in the card snippet: the card is vendor
   * markup rendered from a dozen places (collection, search, home rows, the
   * recommendation strips), and one injector reaches all of them without
   * editing a file we do not own.
   */
  /** The same window applyPreview() re-checks on the client, as a predicate:
   *  data-mb-start/end are unix seconds baked from the BXGY's own dates. */
  function mbWindowOpen(el) {
    var s = Number(el.getAttribute('data-mb-start')), e = Number(el.getAttribute('data-mb-end'));
    if (!s || !e) return true; // no window published — trust the server's mode
    var now = Date.now() / 1000;
    return now >= s && now <= e;
  }

  function eligible() {
    var el = panel();
    if (!el || el.getAttribute('data-multibuy') !== 'on') return null;
    if (!mbWindowOpen(el)) return null;
    var raw = el.getAttribute('data-mb-handles') || '';
    if (!raw) return null;
    return raw.split(',').filter(Boolean);
  }

  /** /products/<handle> — the handle is all we need, and it is in the href of
   *  every card and in the path of every product page. */
  function handleOf(url) {
    var m = String(url || '').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : '';
  }

  function drawPill(handles) {
    if (handleOf(location.pathname) === '' ) return;
    if (handles.indexOf(handleOf(location.pathname)) < 0) return;
    var form = document.querySelector('[data-type="add-to-cart-form"]');
    if (!form || form.querySelector('.kv-mb-pill')) return;
    var btn = form.querySelector('button[name="add"], button[type="submit"]');
    if (!btn) return;
    var a = document.createElement('a');
    a.className = 'kv-mb-pill';
    a.href = '/pages/vegyel-kettot-vigyel-harmat?src=pdp';
    a.innerHTML = '<b>2+1</b><span>Kettőt fizetsz, hármat vihetsz! A harmadik ajándék.</span>';
    // ABOVE the button ROW, not inside it. The add-to-cart button lives in
    // `.flex-buttons`, which is display:flex — dropping the pill in next to it
    // made the two share the row and squeezed "KOSÁRBA TESZ" from full width to
    // 179px. Insert before the row's own container instead, so the pill is a
    // block of its own and the button gets its width back.
    var row = btn.closest ? btn.closest('.flex-buttons') : null;
    var target = row || btn;
    var host = target.parentNode || form;
    host.insertBefore(a, target);
    track('multibuy_pill_shown', 'pill-' + dayKey(), handleOf(location.pathname));
  }

  function drawBadges(handles) {
    var cards = document.querySelectorAll('.product-item');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      if (card.querySelector('.product-item__badge--mb')) continue;
      var link = card.querySelector('a[href*="/products/"]');
      if (!link || handles.indexOf(handleOf(link.getAttribute('href'))) < 0) continue;
      var box = card.querySelector('.product-item__badges');
      if (!box) {
        // A card with no badge rail of its own still gets one, positioned by
        // the theme's own class so it lands where "Kedvenc" and "Újdonság" do.
        box = document.createElement('div');
        box.className = 'product-item__badges';
        var img = card.querySelector('.product-item__image') || card;
        img.appendChild(box);
      }
      var b = document.createElement('span');
      b.className = 'product-item__badge product-item__badge--mb';
      b.textContent = '2+1';
      box.insertBefore(b, box.firstChild);
    }
  }

  /**
   * THE IN-GRID TILE — the other half of mock 5/5. A card-shaped 2+1 panel in
   * the first slot of a collection grid, where a product would be.
   *
   * INSERTED, not substituted: the grid gets one extra cell rather than losing
   * a sellable product. Visually it is the mock — the tile leads the grid — and
   * nothing drops off the page.
   *
   * The grid is found through an eligible CARD rather than by class name, so it
   * works whatever the collection template calls its wrapper, and cards inside
   * the mega-menu are excluded: the menu renders `.product-item` too, and those
   * link to collections, not products.
   */
  function drawTile(handles) {
    if (location.pathname.indexOf('/collections/') !== 0) return;
    if (document.querySelector('.kv-mb-tile')) return;
    var cards = document.querySelectorAll('a.product-item[href*="/products/"], .product-item');
    var grid = null;
    for (var i = 0; i < cards.length; i++) {
      var c = cards[i];
      if (c.closest && c.closest('.mega-menu, .submenu')) continue;
      var link = c.matches && c.matches('a[href*="/products/"]') ? c : c.querySelector('a[href*="/products/"]');
      if (!link) continue;
      if (handles.indexOf(handleOf(link.getAttribute('href'))) < 0) continue;
      grid = c.parentElement;
      break;
    }
    // No eligible product in this collection — the bundle collection, say —
    // means no tile. A 2+1 panel over twelve products the discount does not
    // touch is worse than no panel at all.
    if (!grid) return;
    var a = document.createElement('a');
    a.className = 'kv-mb-tile';
    a.href = '/pages/vegyel-kettot-vigyel-harmat?src=collection';
    a.innerHTML = '<span class="kv-mb-n">2+1</span>'
      + '<p>Kettőt fizetsz, hármat vihetsz! Korlátozott ideig.</p>'
      + '<span class="kv-mb-cta">Így működik</span>';
    grid.insertBefore(a, grid.firstChild);
    track('multibuy_tile_shown', 'tile-' + dayKey(), location.pathname);
  }

  function drawMarks() {
    var handles = eligible();
    if (!handles) return;
    try { drawPill(handles); } catch (e) {}
    try { drawBadges(handles); } catch (e) {}
    try { drawTile(handles); } catch (e) {}
  }

  function boot() {
    // On a page where the section itself is not rendered (the cart page renders
    // only the slot), there is nothing to reconcile against until we fetch it.
    // A page render can also arrive STARVED: Shopify resolves at most 20
    // all_products handles per page, a product page spends them before this
    // section runs, and the tote lookup then comes back blank, so a bundle
    // already in the cart showed no gift rung until the next cart change
    // (seen 2026-10-29 on /products/parkapcsolati-csomag, live theme). A
    // section fetch renders alone, with the full budget.
    var p0 = panel();
    var starved = p0 && p0.getAttribute('data-mode') !== 'off' && p0.getAttribute('data-intl') !== 'true' && !p0.getAttribute('data-gift-legacy');
    if (slot() && !p0) {
      render().then(function () {
        return reconcile();
      });
      return;
    }
    // Starved: keep the normal boot (the cart listeners and marks below are
    // bound only here), but reason about a fresh render, never the starved one.
    if (starved) {
      bindCartEvents();
      drawMarks();
      render().then(function () {
        return reconcile();
      });
      return;
    }
    applyPreview();
    bindPanel();
    hideLegacySlot();
    applyExperiment();
    trackGiftRung();
    trackMultibuyRung();
    trackBundleRung();
    bindCartEvents();
    drawMarks();
    // The first render came from the page itself; only reconcile + recommend.
    reconcile()
      .then(function (mutated) {
        return mutated ? render() : null;
      })
      .then(fillRecommendation)
      .catch(function () {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Test hook. `module` does not exist in a browser, so this is inert in the
  // theme and costs nothing; scripts/cart-gift-reconcile.selftest.ts uses it to
  // drive the gift state machine, which decides what ships in a customer's box
  // and is too easy to break silently from either side (panel or cart).
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { reconcile: reconcile, giftLine: giftLine };
  }
})();

/*
 * /cart recommendations readout (operator 2026-09-29: keep the row, count it
 * for 4 weeks, then keep or remove it on the numbers).
 *   cart_reco_shown   once per page view once the row has product cards:
 *                     count + handles, market, lang
 *   cart_reco_click   a click inside the row: handle, position (1-based),
 *                     action "add" (a quick-buy / add button) or "open"
 * Delegated on document, because <product-recommendations> fills the row by
 * fetch after load. Readout: scripts/cart-reco-readout.ts.
 */
(function () {
  'use strict';
  var EP = 'https://api.korvonal.com/api/storefront-events/cmpismusd0000b965jxyb4r46';
  var ROW = '#cart-recommendations';
  var pageId = String(Date.now()) + Math.random().toString(16).slice(2, 8);
  var shown = false;

  function vid() {
    var m = document.cookie.match(/(?:^|;\s*)_shopify_y=([^;]+)/);
    if (m) return decodeURIComponent(m[1]);
    // Same fallback as korvonal-nav-events: a first-party id when the Shopify
    // cookie is absent (consent declined), so those carts are counted too.
    try {
      var v = localStorage.getItem('otto_visitor_id');
      if (!v) {
        v = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : 'v-' + Date.now() + '-' + Math.random().toString(16).slice(2);
        localStorage.setItem('otto_visitor_id', v);
      }
      return v;
    } catch (e) { return ''; }
  }
  function ctx() {
    var s = window.Shopify || {};
    return { market: s.country || null, lang: (s.locale || document.documentElement.lang || null), path: location.pathname };
  }
  function send(name, suffix, data) {
    var v = vid();
    if (!v) return;
    var d = ctx(); for (var k in data) d[k] = data[k];
    try {
      fetch(EP, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, keepalive: true,
        body: JSON.stringify({ events: [{ id: 'cart-reco:' + v + ':' + pageId + ':' + suffix, eventName: name, visitorId: v, clientId: v, occurredAt: new Date().toISOString(), url: location.href, data: d }] })
      }).catch(function () {});
    } catch (e) {}
  }
  function handleOf(a) {
    var m = String(a && a.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }
  function cards(row) {
    return Array.prototype.slice.call(row.querySelectorAll('.product-item'));
  }
  function checkShown() {
    if (shown) return;
    var row = document.querySelector(ROW);
    if (!row) return;
    var list = cards(row);
    if (!list.length) return;
    shown = true;
    var handles = list.map(function (c) { return handleOf(c.querySelector('a[href*="/products/"]')); }).filter(Boolean);
    send('cart_reco_shown', 'shown', { count: list.length, handles: handles });
  }
  document.addEventListener('click', function (ev) {
    var row = ev.target.closest && ev.target.closest(ROW);
    if (!row) return;
    var card = ev.target.closest('.product-item');
    if (!card) return;
    var pos = cards(row).indexOf(card) + 1;
    var handle = handleOf(card.querySelector('a[href*="/products/"]'));
    var add = ev.target.closest('button, [type="submit"], [data-js-product-add-to-cart], .product-item__quick-buy');
    send('cart_reco_click', 'click:' + pos + ':' + Date.now(), { handle: handle, position: pos, action: add ? 'add' : 'open' });
  }, true);
  if (document.querySelector(ROW)) {
    new MutationObserver(checkShown).observe(document.querySelector(ROW), { childList: true, subtree: true });
    checkShown();
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      var row = document.querySelector(ROW);
      if (!row) return;
      new MutationObserver(checkShown).observe(row, { childList: true, subtree: true });
      checkShown();
    });
  }
})();
